import moment from 'moment';
import { isEmpty } from 'lodash';
import { GET_ALL_LANGUAGE } from '@gql/talentLanguages';
import { GET_TALENT } from '@gql/talents';
import { initializeApollo } from '../../../../../apollo';
import { confirm } from '@components/ConfirmModal/confirm';
import {
    TalentLanguage,
    Language,
    LanguageFormValues,
    LanguageOption,
} from '../types';

const LANGUAGE_OPTIONS_CACHE_KEY = 'optionLanguages';
const LANGUAGE_OPTIONS_CREATED_AT_KEY = 'optionLanguagesCreatedAt';
const CACHE_TTL_HOURS = 1;

/** Build default react-hook-form values from an existing TalentLanguage record. */
export const getDefaultFormValues = (selectedData: TalentLanguage | null): LanguageFormValues => {
    const entry = selectedData?.languages?.[0];
    const itemLanguage = !isEmpty(entry?.language) ? entry.language[0] : null;
    const itemCertificate = !isEmpty(entry?.certificates) ? entry.certificates[0] : null;
    const itemLevel = !isEmpty(entry?.level) ? entry.level[0] : null;

    return {
        language: itemLanguage?._id ?? null,
        level: itemLevel?._id ?? null,
        issuer: itemCertificate?.issuer?.name ?? null,
        title: itemCertificate?.title ?? null,
        number: itemCertificate?.number ?? null,
        url: itemCertificate?.url ?? null,
        obtainedResult: itemCertificate?.obtainedResult?.toString() ?? null,
        startDate: itemCertificate?.startDate
            ? moment(itemCertificate.startDate).format('YYYY-MM-DD')
            : null,
        endDate: itemCertificate?.endDate
            ? moment(itemCertificate.endDate).format('YYYY-MM-DD')
            : null,
        file: null,
        certificateLanguages: itemCertificate?._id ? [itemCertificate._id] : [],
    };
};

/**
 * Fetch all available language options, with a 1-hour localStorage cache.
 * Results are sorted alphabetically by label.
 */
export const fetchLanguageOptions = async (): Promise<LanguageOption[]> => {
    const apolloClient = initializeApollo(null, null);
    const createdAt = moment(localStorage.getItem(LANGUAGE_OPTIONS_CREATED_AT_KEY));
    const hours = moment.duration(moment().diff(createdAt)).asHours();

    const cached = localStorage.getItem(LANGUAGE_OPTIONS_CACHE_KEY);
    let options: LanguageOption[] = !isEmpty(cached) ? JSON.parse(cached) : [];

    if (isEmpty(options) || hours > CACHE_TTL_HOURS) {
        const response = await apolloClient.query({
            variables: {},
            query: GET_ALL_LANGUAGE,
        });
        const listData: Language[] = response?.data?.getAllLanguage ?? [];
        options = listData
            .map(item => ({
                label: item?.name?.toUpperCase() ?? '',
                value: item?._id,
                code: item?.code,
            }))
            .sort((a, b) => a.label.localeCompare(b.label));

        localStorage.setItem(LANGUAGE_OPTIONS_CACHE_KEY, JSON.stringify(options));
        localStorage.setItem(LANGUAGE_OPTIONS_CREATED_AT_KEY, moment().toISOString());
    }

    return options;
};

/** Prompt the user for confirmation then delete a talent language entry. */
export const handleDeleteLanguage = async (
    id: string,
    deleteLanguage: (opts: object) => void,
): Promise<void> => {
    if (await confirm({ confirmation: 'Êtes-vous sûr de vouloir supprimer cette langue?' })) {
        deleteLanguage({
            variables: { id },
            refetchQueries: () => [{ query: GET_TALENT }],
        });
    }
};

/**
 * Format a certificate validity period as a human-readable string.
 * @example "Obtention Jan 2022 - Expire Dec 2025"
 * @example "Obtention Jan 2022 - Valide à vie"
 */
export const formatCertificateDateRange = (startDate: string, endDate: string | null): string => {
    const start = `Obtention ${moment(startDate).format('MMM YYYY')}`;
    const end = endDate ? `Expire ${moment(endDate).format('MMM YYYY')}` : 'Valide à vie';
    return `${start} - ${end}`;
};
